import {inject, Injectable, Provider} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {merge, map, startWith} from 'rxjs';
import {AUR_CALENDAR_LOCALE, CalendarLocale, CALENDAR_LOCALE_EN} from '../../aur-datepicker/public-api';

/** Адаптер языка портала: Intl даёт календарные названия, переводы — доступные подписи навигации. */
@Injectable({providedIn: 'root'})
export class AppCalendarLocaleService {
  private readonly translate = inject(TranslateService);
  public readonly locale = toSignal(merge(
    this.translate.onLangChange,
    this.translate.onTranslationChange,
    this.translate.onDefaultLangChange,
  ).pipe(startWith(null), map(() => this.createLocale())), {requireSync: true});

  private createLocale(): CalendarLocale {
    const language = this.translate.currentLang || this.translate.defaultLang || 'en';
    const months = new Intl.DateTimeFormat(language, {month: 'long', timeZone: 'UTC'});
    const shortDays = new Intl.DateTimeFormat(language, {weekday: 'short', timeZone: 'UTC'});
    const fullDays = new Intl.DateTimeFormat(language, {weekday: 'long', timeZone: 'UTC'});
    const title = (text: string) => text.charAt(0).toLocaleUpperCase(language) + text.slice(1);
    const label = (key: string, fallback: string): string => {
      const path = `date-calendar.${key}`;
      const translated = this.translate.instant(path);
      return translated === path ? fallback : translated;
    };
    return {
      months: Array.from({length: 12}, (_, month) => title(months.format(Date.UTC(2024, month, 1)))),
      // 1 января 2024 — понедельник; UTC исключает зависимость подписей от зоны браузера.
      weekdays: Array.from({length: 7}, (_, day) => title(shortDays.format(Date.UTC(2024, 0, day + 1)))),
      weekdayLabels: Array.from({length: 7}, (_, day) => title(fullDays.format(Date.UTC(2024, 0, day + 1)))),
      previousMonth: label('previous-month', CALENDAR_LOCALE_EN.previousMonth),
      nextMonth: label('next-month', CALENDAR_LOCALE_EN.nextMonth),
      previousYear: label('previous-year', CALENDAR_LOCALE_EN.previousYear),
      nextYear: label('next-year', CALENDAR_LOCALE_EN.nextYear),
      previousYears: label('previous-years', CALENDAR_LOCALE_EN.previousYears),
      nextYears: label('next-years', CALENDAR_LOCALE_EN.nextYears),
    };
  }
}

/** Один корневой провайдер на портал; работает также в overlay и lazy-модулях. */
export function provideAppCalendarLocale(): Provider {
  return {provide: AUR_CALENDAR_LOCALE, useFactory: () => inject(AppCalendarLocaleService).locale};
}
